import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  MagnifyingGlass,
  X,
  PaperPlaneTilt,
  Spinner,
  CheckCircle,
  WarningCircle,
  ArrowClockwise,
  FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CustomerDeliveryProfilePanel from "@/components/CustomerDeliveryProfilePanel";

const PRIORITIES = ["Today", "Tomorrow", "This Week", "Next Week", "Routine"];

const PRODUCT_CODES = {
  "premium gasoline": "102",
  "regular gasoline": "104",
  "clear diesel": "122",
  "dyed diesel": "126",
  "furnace oil": "157",
  "bulk def": "DEF",
  "bulk lubricants": "LUBE",
  "propane": "PROP",
};
const codeFor = (name) => PRODUCT_CODES[(name || "").trim().toLowerCase()] || "";

const PROPANE_MESSAGE =
  "Propane orders are fulfilled separately by Core Fuels and must be entered as a separate order.";

export default function NewOrder() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("edit");
  const duplicateId = searchParams.get("duplicate");
  const prefillAccount = searchParams.get("account");  // deep-link from Customer 360 → "Start New Order"
  const isEdit = !!editId;

  // --- Customer search ---
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const [customer, setCustomer] = useState(null);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  // Non-blocking warning when the picked customer has a very recent order —
  // shielded against accidental duplicates during phone-heavy shifts.
  const [recentOrderWarning, setRecentOrderWarning] = useState(null);
  const RECENT_ORDER_WINDOW_MIN = 15;

  // --- Order fields ---
  const [products, setProducts] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectedDrivers, setSelectedDrivers] = useState({});
  const [priority, setPriority] = useState("Routine");
  const [notes, setNotes] = useState("");
  const [useAltLocation, setUseAltLocation] = useState(false);
  const [altLocation, setAltLocation] = useState("");
  const [directions, setDirections] = useState("");

  // --- Send / status dialog ---
  const [sending, setSending] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultOrder, setResultOrder] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [editLoaded, setEditLoaded] = useState(false);

  // --- Recent customers + Saved templates ---
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Fetch recent customers + templates once on mount
  useEffect(() => {
    if (isEdit) return;
    (async () => {
      try {
        const [r, t] = await Promise.all([
          api.get("/customers/recent"),
          api.get("/order-templates"),
        ]);
        setRecentCustomers(r.data);
        setTemplates(t.data);
      } catch {
        // silent — both panels are optional
      }
    })();
  }, [isEdit]);

  // Debounce customer search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (customer || !debounced.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setSearching(true);
      try {
        const res = await api.get("/customers", {
          params: { q: debounced, status: "Active" },
        });
        if (!cancelled) {
          setResults(res.data.slice(0, 8));
          setShowResults(true);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, customer]);

  useEffect(() => {
    (async () => {
      try {
        const [p, d] = await Promise.all([
          api.get("/products", { params: { status: "Active" } }),
          api.get("/drivers", { params: { status: "Active" } }),
        ]);
        setProducts(p.data);
        setDrivers(d.data);
      } catch {
        toast.error("Failed to load products/drivers");
      }
    })();
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Edit / Duplicate prefill — wait until products + drivers have loaded so
  // checkbox keys match the lookup IDs.
  useEffect(() => {
    const sourceId = editId || duplicateId;
    if (!sourceId || editLoaded) return;
    if (products.length === 0 || drivers.length === 0) return;
    (async () => {
      try {
        const orderRes = await api.get(`/orders/${sourceId}`);
        const o = orderRes.data;
        try {
          const fresh = await api.get(`/customers/${o.customer_id}`);
          setCustomer(fresh.data);
        } catch {
          setCustomer({
            id: o.customer_id,
            account_number: o.account_number,
            customer_name: o.customer_name,
            mailing_address: o.mailing_address,
            city: "",
            province: "",
            postal_code: "",
            phone_number: o.phone_number,
            email_address: "",
          });
        }
        // Pre-select products by id
        const psel = {};
        for (const op of o.products || []) {
          const match = products.find((p) => p.id === op.product_id);
          if (match) psel[match.id] = { product: match };
        }
        setSelectedProducts(psel);
        // Pre-select drivers by id (drivers from subcontractor rules like
        // Core Fuels won't appear in the drivers list and are re-applied server-side)
        const dsel = {};
        for (const id of o.driver_ids || []) {
          const match = drivers.find((d) => d.id === id);
          if (match) dsel[match.id] = match;
        }
        setSelectedDrivers(dsel);
        setPriority(o.priority || "Routine");
        setNotes(o.delivery_notes || "");
        setUseAltLocation(!!o.alternate_delivery_location);
        setAltLocation(o.alternate_delivery_location || "");
        setDirections(o.directions || "");
        setEditLoaded(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load order");
      }
    })();
  }, [editId, duplicateId, products, drivers, editLoaded]);

  const selectCustomer = (c) => {
    setCustomer(c);
    setQuery("");
    setResults([]);
    setShowResults(false);
    setHighlightIdx(-1);
    // Non-blocking duplicate-order check — glance-only warning banner.
    if (c?.id && !isEdit) {
      api.get(`/customers/${c.id}/dispatches`, { params: { limit: 1 } })
        .then((r) => {
          const latest = (r.data || [])[0];
          if (!latest) { setRecentOrderWarning(null); return; }
          const ageMin = Math.round(
            (Date.now() - new Date(latest.created_at).getTime()) / 60000,
          );
          if (ageMin >= 0 && ageMin < RECENT_ORDER_WINDOW_MIN) {
            setRecentOrderWarning({
              order_number: latest.order_number,
              order_id: latest.id,
              status: latest.status,
              created_by: latest.created_by || "unknown user",
              age_minutes: ageMin,
              products: (latest.products || []).map((p) => p.product_name),
            });
          } else {
            setRecentOrderWarning(null);
          }
        })
        .catch(() => setRecentOrderWarning(null));
    } else {
      setRecentOrderWarning(null);
    }
  };

  const clearForm = () => {
    setCustomer(null);
    setSelectedProducts({});
    setSelectedDrivers({});
    setPriority("Routine");
    setNotes("");
    setUseAltLocation(false);
    setAltLocation("");
    setDirections("");
    setRecentOrderWarning(null);
    setHighlightIdx(-1);
  };

  // --- P2 · Rapid Order Entry keyboard support ---
  // Global `/` shortcut focuses the customer search bar. Skipped when the user
  // is already typing in a form field so it never steals characters.
  useEffect(() => {
    if (isEdit || customer) return;
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isEdit, customer]);

  // Keep the visible highlight bounded when the result set changes.
  useEffect(() => {
    if (highlightIdx >= results.length) setHighlightIdx(results.length - 1);
  }, [results, highlightIdx]);

  const handleSearchKeyDown = (e) => {
    if (!showResults || results.length === 0) {
      if (e.key === "Escape") { setQuery(""); setShowResults(false); }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = highlightIdx >= 0 ? results[highlightIdx] : results[0];
      if (pick) selectCustomer(pick);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setQuery("");
      setShowResults(false);
      setHighlightIdx(-1);
    }
  };

  // Deep-link support: /new-order?account=<erp_account> pre-selects the
  // customer (used by Customer 360 drawer's "Start New Order" CTA).
  useEffect(() => {
    if (isEdit || customer || !prefillAccount) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/customers/by-account/${encodeURIComponent(prefillAccount)}`);
        if (cancelled) return;
        if (r?.data?.id) selectCustomer(r.data);
      } catch {
        // silent — user can still search
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillAccount, isEdit]);

  const productList = useMemo(() => Object.values(selectedProducts), [selectedProducts]);
  const driverList = useMemo(() => Object.values(selectedDrivers), [selectedDrivers]);

  // Business rule: Propane cannot be combined with other products.
  const isPropane = (p) => p.product_name.trim().toLowerCase() === "propane";
  const hasPropaneSelected = productList.some((s) => isPropane(s.product));
  const hasNonPropaneSelected = productList.some((s) => !isPropane(s.product));

  const toggleProduct = (product) => {
    const propane = isPropane(product);
    setSelectedProducts((prev) => {
      const next = { ...prev };
      if (next[product.id]) {
        delete next[product.id];
        return next;
      }
      // Adding Propane while others are selected → block
      if (propane && Object.keys(next).length > 0) {
        toast.error(PROPANE_MESSAGE);
        return next;
      }
      // Adding any other product while Propane is selected → block
      if (!propane && Object.values(next).some((s) => isPropane(s.product))) {
        toast.error(PROPANE_MESSAGE);
        return next;
      }
      next[product.id] = { product };
      return next;
    });
  };

  // (removed) quantity is no longer captured at order entry — drivers record delivered amount in ERP.

  const toggleDriver = (driver) => {
    setSelectedDrivers((prev) => {
      const next = { ...prev };
      if (next[driver.id]) delete next[driver.id];
      else next[driver.id] = driver;
      return next;
    });
  };

  // Apply a saved template — pre-fills the form (customer is fetched fresh so
  // the address reflects whatever the latest ERP import has).
  const applyTemplate = async (tpl) => {
    try {
      const fresh = await api.get(`/customers/${tpl.customer_id}`);
      setCustomer(fresh.data);
    } catch {
      // Fallback: synthesize a minimal customer from the template snapshot.
      setCustomer({
        id: tpl.customer_id,
        account_number: tpl.account_number,
        customer_name: tpl.customer_name,
        mailing_address: "",
        city: "", province: "", postal_code: "",
        phone_number: "",
        email_address: "",
      });
    }
    const psel = {};
    for (const op of tpl.products || []) {
      const match = products.find((p) => p.id === op.product_id);
      if (match) psel[match.id] = { product: match };
    }
    setSelectedProducts(psel);
    const dsel = {};
    for (const id of tpl.driver_ids || []) {
      const match = drivers.find((d) => d.id === id);
      if (match) dsel[match.id] = match;
    }
    setSelectedDrivers(dsel);
    setPriority(tpl.priority || "Routine");
    toast.success(`Template "${tpl.name}" applied`);
  };

  // Pick a recent customer (one-click select, skipping the search step)
  const pickRecentCustomer = async (c) => {
    try {
      const fresh = await api.get(`/customers/${c.id}`);
      selectCustomer(fresh.data);
    } catch {
      selectCustomer(c);
    }
  };

  const saveCurrentAsTemplate = async () => {
    if (!customer) return toast.error("Pick a customer first");
    if (Object.keys(selectedProducts).length === 0) return toast.error("Select at least one product");
    const name = templateName.trim();
    if (!name) return toast.error("Give the template a name");
    setSavingTemplate(true);
    try {
      const driverList = Object.values(selectedDrivers);
      const payload = {
        name,
        customer_id: customer.id,
        customer_name: customer.customer_name,
        account_number: customer.account_number,
        products: Object.values(selectedProducts).map((s) => ({
          product_id: s.product.id,
          product_name: s.product.product_name,
        })),
        driver_ids: driverList.map((d) => d.id),
        driver_names: driverList.map((d) => d.driver_name),
        driver_emails: driverList.map((d) => d.email_address),
        priority,
      };
      const res = await api.post("/order-templates", payload);
      setTemplates((prev) => [res.data, ...prev]);
      setTemplateName("");
      toast.success(`Template "${name}" saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/order-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((x) => x.id !== tpl.id));
      toast.success("Template deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete template");
    }
  };

  const mailingAddress = customer
    ? `${customer.mailing_address}, ${customer.city}, ${customer.province} ${customer.postal_code}`
    : "";

  // For Propane-only orders, no Coolman driver is required (Core Fuels is the recipient).
  const driverRequired = !hasPropaneSelected || hasNonPropaneSelected;

  const canSend =
    !!customer &&
    productList.length > 0 &&
    (!driverRequired || driverList.length > 0);

  const sendOrder = async () => {
    if (!customer) return toast.error("Please select a customer first");
    if (productList.length === 0) return toast.error("Select at least one product");
    if (driverRequired && driverList.length === 0) return toast.error("Select at least one driver");

    setSending(true);
    setResultOpen(true);
    setResultOrder(null);
    try {
      const payload = {
        customer_id: customer.id,
        customer_name: customer.customer_name,
        account_number: customer.account_number,
        mailing_address: mailingAddress,
        phone_number: customer.phone_number,
        products: productList.map((s) => ({
          product_id: s.product.id,
          product_name: s.product.product_name,
        })),
        driver_ids: driverList.map((d) => d.id),
        driver_names: driverList.map((d) => d.driver_name),
        driver_emails: driverList.map((d) => d.email_address),
        priority,
        delivery_notes: notes,
        alternate_delivery_location: useAltLocation ? altLocation : "",
        directions: useAltLocation ? directions : "",
      };
      const res = isEdit
        ? await api.put(`/orders/${editId}`, payload)
        : await api.post("/orders", payload);
      setResultOrder(res.data);
    } catch (e) {
      setResultOpen(false);
      toast.error(e?.response?.data?.detail || (isEdit ? "Failed to save" : "Failed to send order"));
    } finally {
      setSending(false);
    }
  };

  const retrySend = async () => {
    if (!resultOrder) return;
    setRetrying(true);
    try {
      const res = await api.post(`/orders/${resultOrder.id}/resend-notification`);
      setResultOrder(res.data);
      if (res.data.notification_status === "Sent") {
        toast.success("Email notification delivered");
      } else {
        toast.error("Email send failed again");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(false);
    }
  };

  const closeResult = () => {
    setResultOpen(false);
    setResultOrder(null);
    if (isEdit) {
      navigate("/todays-orders");
    } else {
      clearForm();
    }
  };

  return (
    <div data-testid="new-order-page" className="pb-32">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
          {isEdit ? "Edit Order" : duplicateId ? "Duplicate Order" : "New Order"}
        </h1>
        <p className="mt-1.5 text-[15px] text-gray-600">
          {isEdit
            ? "Update order details. Saving will resend the email to the assigned drivers if the order was already dispatched."
            : "Find the customer, choose products and drivers, then send the order."}
        </p>
      </div>

      {/* ===== Customer ===== */}
      <Section step="1" title="Find Customer">
        {!customer ? (
          <>
            {/* Templates strip — only when there's at least one saved template */}
            {!isEdit && templates.length > 0 && (
              <div className="mb-3" data-testid="templates-strip">
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
                  Saved Order Templates
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((tpl) => (
                    <div
                      key={tpl.id}
                      data-testid={`template-chip-${tpl.id}`}
                      className="group inline-flex h-8 items-center gap-1 rounded-full border border-gray-300 bg-white pl-3 pr-1 text-sm hover:bg-gray-50"
                    >
                      <button
                        onClick={() => applyTemplate(tpl)}
                        className="flex items-center gap-1.5 pr-1 text-gray-800 hover:text-[#1A1A1A]"
                        title={`${tpl.customer_name} · ${tpl.products.map(p => p.product_name).join(", ")}`}
                      >
                        <span className="font-medium">{tpl.name}</span>
                      </button>
                      <button
                        onClick={() => deleteTemplate(tpl)}
                        title={`Delete template "${tpl.name}"`}
                        className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-[#EB1D25] group-hover:opacity-100"
                      >
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent customers strip */}
            {!isEdit && recentCustomers.length > 0 && (
              <div className="mb-3" data-testid="recent-customers-strip">
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
                  Recent Customers
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {recentCustomers.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`recent-customer-${c.account_number}`}
                      onClick={() => pickRecentCustomer(c)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-full border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:border-[#1A1A1A] hover:bg-gray-50"
                      title={c.account_number}
                    >
                      {c.customer_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

          <div ref={searchRef} className="relative">
            <MagnifyingGlass size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="new-order-customer-search"
              ref={searchInputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlightIdx(-1); }}
              onFocus={() => results.length && setShowResults(true)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by name, account #, phone, or mailing address…   ( / to focus, ↑↓ to navigate, ↵ to select )"
              className="h-12 w-full rounded-md border border-gray-300 bg-white pl-11 pr-10 text-base focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
              autoFocus
            />
            {searching && (
              <Spinner size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-500" />
            )}

            {showResults && results.length > 0 && (
              <div
                data-testid="customer-search-results"
                className="absolute left-0 right-0 top-full z-20 mt-1.5 max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
              >
                {results.map((c, idx) => {
                  const active = idx === highlightIdx;
                  return (
                    <button
                      key={c.id}
                      data-testid={`customer-result-${c.account_number}`}
                      onClick={() => selectCustomer(c)}
                      onMouseEnter={() => setHighlightIdx(idx)}
                      className={`flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 ${
                        active ? "bg-[#f5f6f8]" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="font-mono text-sm font-semibold text-gray-600">#{c.account_number}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900">{c.customer_name}</div>
                        <div className="truncate text-sm text-gray-500">
                          {c.mailing_address}, {c.city} · {c.phone_number}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {showResults && !searching && debounced && results.length === 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1.5 rounded-md border border-gray-200 bg-white p-4 text-center text-[15px] text-gray-500 shadow-lg">
                No customers match &ldquo;{debounced}&rdquo;
              </div>
            )}
          </div>
          </>
        ) : (
          <>
            {/* Duplicate-order warning — non-blocking. Set by selectCustomer()
                when the picked customer has an order created in the last
                RECENT_ORDER_WINDOW_MIN minutes. */}
            {recentOrderWarning && (
              <div
                data-testid="duplicate-order-warning"
                className="mb-3 flex items-start justify-between gap-3 rounded-md border border-status-warning-bg bg-status-warning-bg/70 px-4 py-2.5 text-sm text-status-warning-fg"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-semibold">
                    <WarningCircle size={14} weight="bold" /> Possible duplicate order
                  </div>
                  <div className="mt-0.5">
                    Order <span className="font-mono font-semibold">{recentOrderWarning.order_number}</span>
                    {" "}was created {recentOrderWarning.age_minutes} min ago by{" "}
                    <span className="font-medium">{recentOrderWarning.created_by}</span>
                    {recentOrderWarning.products.length > 0 && (
                      <> · {recentOrderWarning.products.join(", ")}</>
                    )}
                    {" "}· status <span className="font-medium">{recentOrderWarning.status}</span>. Continue only if this is a separate delivery.
                  </div>
                </div>
                <button
                  data-testid="dismiss-duplicate-warning"
                  onClick={() => setRecentOrderWarning(null)}
                  className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-status-warning-fg hover:bg-status-warning-bg"
                  aria-label="Dismiss warning"
                >
                  Dismiss
                </button>
              </div>
            )}

          <div data-testid="selected-customer" className="rounded-md border border-gray-200 bg-[#f3f4f6] p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="grid flex-1 grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                <Info label="Account Number" value={customer.account_number} mono />
                <Info label="Customer Name" value={customer.customer_name} bold />
                <Info label="Mailing Address" value={mailingAddress} />
                <Info label="Phone Number" value={customer.phone_number} mono />
                <Info label="Email Address" value={customer.email_address} />
              </div>
              <button
                data-testid="change-customer-button"
                onClick={clearForm}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <X size={14} weight="bold" /> Change
              </button>
            </div>
            <CustomerDeliveryProfilePanel accountNumber={customer.account_number} />
          </div>
          </>
        )}
      </Section>

      {/* ===== Order ===== */}
      <Section step="2" title="Order Details" disabled={!customer}>
        {/* Delivery Requested — single-select toggle */}
        <Field label="Delivery Requested">
          <div
            data-testid="delivery-requested-group"
            role="radiogroup"
            className="grid grid-cols-2 gap-2 sm:grid-cols-5"
          >
            {PRIORITIES.map((p) => {
              const active = priority === p;
              const isToday = p === "Today";
              return (
                <button
                  key={p}
                  data-testid={`priority-${p.toLowerCase().replace(/\s+/g, "-")}`}
                  role="radio"
                  aria-checked={active}
                  onClick={() => setPriority(p)}
                  className={`h-10 rounded-md border text-sm font-semibold transition-colors ${
                    active
                      ? isToday
                        ? "border-[#EB1D25] bg-[#EB1D25] text-white"
                        : "border-[#1A1A1A] bg-[#1A1A1A] text-white"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </Field>

        {/* Products */}
        <div className="mt-6">
          <div className="mb-2 text-sm font-medium text-gray-700">Products</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {products.map((p) => {
              const sel = !!selectedProducts[p.id];
              const propane = p.product_name.trim().toLowerCase() === "propane";
              const disabled =
                !sel &&
                ((propane && hasNonPropaneSelected) || (!propane && hasPropaneSelected));
              const code = codeFor(p.product_name);
              return (
                <div
                  key={p.id}
                  data-testid={`product-option-${p.id}`}
                  className={`relative flex items-center gap-3 rounded-md border px-3 py-2 ${
                    sel
                      ? "border-[#1A1A1A] bg-[#f3f4f6]"
                      : disabled
                        ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    data-testid={`product-checkbox-${p.id}`}
                    checked={sel}
                    disabled={disabled}
                    onChange={() => toggleProduct(p)}
                    className="h-5 w-5 accent-[#1A1A1A] disabled:cursor-not-allowed"
                  />
                  <button
                    onClick={() => !disabled && toggleProduct(p)}
                    disabled={disabled}
                    className="flex-1 pr-12 text-left"
                  >
                    <div className="text-[15px] font-medium text-gray-900">
                      {p.product_name}
                      {propane && (
                        <span className="ml-2 inline-block rounded-sm bg-[#EB1D25]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#EB1D25]">
                          Core Fuels
                        </span>
                      )}
                    </div>
                  </button>
                  {code && (
                    <span
                      data-testid={`product-code-${p.id}`}
                      className="absolute right-2 top-2 rounded-sm bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-500"
                    >
                      {code}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Drivers checklist */}
        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-gray-700">
              Assign Driver(s){" "}
              {driverRequired ? (
                <span className="text-gray-500">— select one or more</span>
              ) : (
                <span className="text-[#EB1D25]">— not required for Propane (Core Fuels handles delivery)</span>
              )}
            </div>
          </div>
          {hasPropaneSelected && !hasNonPropaneSelected && (
            <div className="mb-3 rounded-md border border-[#EB1D25]/30 bg-[#EB1D25]/5 p-3 text-sm text-[#1A1A1A]">
              {PROPANE_MESSAGE}
            </div>
          )}
          {drivers.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">
              No active drivers. Add drivers under the Drivers page.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {drivers.map((d) => {
                const sel = !!selectedDrivers[d.id];
                return (
                  <label
                    key={d.id}
                    data-testid={`driver-option-${d.id}`}
                    className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border px-3 ${
                      sel
                        ? "border-[#1A1A1A] bg-[#f3f4f6]"
                        : "border-gray-300 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`driver-checkbox-${d.id}`}
                      checked={sel}
                      onChange={() => toggleDriver(d)}
                      className="h-4 w-4 accent-[#1A1A1A]"
                    />
                    <span className="text-sm font-medium text-gray-900">{d.driver_name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Delivery Notes */}
        <Field label="Delivery Notes" className="mt-6">
          <textarea
            data-testid="order-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Gate code, tank location, dog on property, etc."
            rows={2}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-[15px] focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
          />
        </Field>

        {/* Alternate Delivery */}
        <div className="mt-6 rounded-md border border-gray-200 bg-[#f3f4f6] p-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              data-testid="alt-location-toggle"
              checked={useAltLocation}
              onChange={(e) => setUseAltLocation(e.target.checked)}
              className="mt-0.5 h-5 w-5 accent-[#1A1A1A]"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                Alternate Delivery Location
              </div>
              <div className="text-sm text-gray-500">
                Use this if the delivery is going somewhere other than the customer&apos;s mailing address.
              </div>
            </div>
          </label>

          {useAltLocation && (
            <div className="mt-4 grid grid-cols-1 gap-4">
              <Field label="Location Description">
                <input
                  data-testid="alt-location-input"
                  value={altLocation}
                  onChange={(e) => setAltLocation(e.target.value)}
                  placeholder="e.g. East field tank, behind barn"
                  className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-[15px] focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
                />
              </Field>
              <Field label="Directions / Notes">
                <textarea
                  data-testid="alt-directions-input"
                  value={directions}
                  onChange={(e) => setDirections(e.target.value)}
                  placeholder="Turn at the white silo, follow lane 200m past the house…"
                  rows={2}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-[15px] focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
                />
              </Field>
            </div>
          )}
        </div>
      </Section>

      {/* Send button */}
      {/* Save as Template — only when not editing and the form has substance */}
      {!isEdit && customer && Object.keys(selectedProducts).length > 0 && (
        <div
          data-testid="save-template-row"
          className="mt-6 flex flex-col items-stretch gap-2 rounded-md border border-dashed border-gray-300 bg-[#f9fafb] p-3 sm:flex-row sm:items-center"
        >
          <span className="text-xs font-medium text-gray-600 sm:w-44">
            Save this as a reusable template?
          </span>
          <input
            data-testid="template-name-input"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder='e.g. "Smiths Monthly Furnace Oil"'
            className="h-9 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
          />
          <button
            data-testid="save-template-button"
            onClick={saveCurrentAsTemplate}
            disabled={savingTemplate || !templateName.trim()}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {savingTemplate ? (
              <Spinner size={12} className="animate-spin" />
            ) : (
              <FloppyDisk size={12} weight="bold" />
            )}
            Save Template
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center">
        <div className="text-sm text-gray-500">
          {customer ? (
            canSend ? (
              <span>
                <CheckCircle size={16} weight="fill" className="-mt-0.5 mr-1 inline text-green-600" />
                Ready to send · {productList.length} product{productList.length > 1 ? "s" : ""} · {driverList.length} driver{driverList.length > 1 ? "s" : ""}
              </span>
            ) : (
              "Complete the form to enable sending."
            )
          ) : (
            "Find a customer to begin."
          )}
        </div>
        <button
          data-testid="send-order-button"
          onClick={sendOrder}
          disabled={!canSend || sending}
          className="inline-flex h-16 min-w-[280px] items-center justify-center gap-2 rounded-md bg-[#EB1D25] px-8 text-lg font-semibold text-white shadow-sm hover:bg-[#AF101B] disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {sending ? (
            <>
              <Spinner size={20} className="animate-spin" /> {isEdit ? "Saving…" : "Sending…"}
            </>
          ) : isEdit ? (
            <>
              <FloppyDisk size={20} weight="bold" />
              SAVE CHANGES
            </>
          ) : (
            <>
              <PaperPlaneTilt size={20} weight="bold" />
              SEND ORDER
            </>
          )}
        </button>
      </div>

      <SendOrderDialog
        open={resultOpen}
        sending={sending}
        order={resultOrder}
        retrying={retrying}
        onRetry={retrySend}
        onClose={closeResult}
      />
    </div>
  );
}

function Section({ step, title, disabled, children }) {
  return (
    <div className={`mt-5 border border-gray-200 bg-white p-6 ${disabled ? "pointer-events-none opacity-50" : ""}`}>
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1A1A1A] text-sm font-bold text-white">
          {step}
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function Info({ label, value, mono, bold }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`mt-0.5 text-[15px] text-gray-900 ${mono ? "font-mono" : ""} ${bold ? "font-semibold" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function SendOrderDialog({ open, sending, order, retrying, onRetry, onClose }) {
  // The dialog opens as soon as Send Order is clicked. It walks through three
  // states: in-flight → success → failure (with Retry Send).
  const status = !order
    ? "sending"
    : order.notification_status === "Sent"
      ? "success"
      : "failed";

  return (
    <Dialog open={open} onOpenChange={(v) => (!v && status !== "sending" ? onClose() : null)}>
      <DialogContent
        data-testid="send-order-dialog"
        className="sm:max-w-md"
        onInteractOutside={(e) => status === "sending" && e.preventDefault()}
        onEscapeKeyDown={(e) => status === "sending" && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">Sending order</DialogTitle>
        </DialogHeader>

        {status === "sending" && (
          <div
            data-testid="send-order-progress"
            className="flex flex-col items-center justify-center gap-4 py-10 text-center"
          >
            <Spinner size={40} className="animate-spin text-[#1A1A1A]" />
            <div className="text-lg font-semibold text-gray-900">Sending Order…</div>
            <div className="text-sm text-gray-500">
              Saving the order and notifying the assigned driver(s).
            </div>
          </div>
        )}

        {status === "success" && order && (
          <div
            data-testid="send-order-success"
            className="flex flex-col items-center justify-center gap-3 py-8 text-center"
          >
            <CheckCircle size={56} weight="fill" className="text-green-600" />
            <div className="text-lg font-semibold text-gray-900">
              Order successfully sent
            </div>
            <div className="text-sm text-gray-600">
              Email notifications have been delivered.
            </div>
            <div className="mt-2 rounded-md border border-gray-200 bg-[#f3f4f6] px-4 py-2 text-sm">
              <span className="font-mono font-semibold text-gray-900">
                {order.order_number}
              </span>{" "}
              <span className="text-gray-500">·</span>{" "}
              <span className="text-gray-700">{order.customer_name}</span>
            </div>
            <div className="mt-4 flex w-full justify-end">
              <button
                data-testid="send-order-done"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-md bg-[#1A1A1A] px-5 py-2 text-sm font-semibold text-white hover:bg-[#4D4D4D]"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {status === "failed" && order && (
          <div
            data-testid="send-order-failed"
            className="flex flex-col items-center justify-center gap-3 py-8 text-center"
          >
            <WarningCircle size={56} weight="fill" className="text-[#EB1D25]" />
            <div className="text-lg font-semibold text-gray-900">
              Order saved successfully
            </div>
            <div className="text-sm text-gray-600">
              Email notification could not be sent. Please retry sending notifications.
            </div>
            <div className="mt-2 rounded-md border border-gray-200 bg-[#f3f4f6] px-4 py-2 text-sm">
              <span className="font-mono font-semibold text-gray-900">
                {order.order_number}
              </span>{" "}
              <span className="text-gray-500">·</span>{" "}
              <span className="text-gray-700">{order.customer_name}</span>
            </div>
            {order.last_send_error && (
              <div
                data-testid="send-order-error-detail"
                className="mt-1 max-h-24 w-full overflow-y-auto rounded-md border border-red-200 bg-red-50/50 px-3 py-2 text-left text-xs text-red-700"
              >
                {order.last_send_error}
              </div>
            )}
            <div className="mt-4 flex w-full items-center justify-end gap-2">
              <button
                data-testid="send-order-close-failed"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                data-testid="send-order-retry"
                onClick={onRetry}
                disabled={retrying}
                className="inline-flex items-center gap-2 rounded-md bg-[#EB1D25] px-5 py-2 text-sm font-semibold text-white hover:bg-[#AF101B] disabled:bg-gray-300"
              >
                {retrying ? (
                  <>
                    <Spinner size={14} className="animate-spin" /> Retrying…
                  </>
                ) : (
                  <>
                    <ArrowClockwise size={14} weight="bold" /> Retry Send
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
